import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { APD_FIELDS, StagePlanStage } from "@/lib/basProcess/constants";
import { CalendarRange, Plus, Trash2, FileDown } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import { generateStagePlanWordDocument } from "@/lib/documentGenerator/samordningWordGenerator";

interface Props {
  projectId: string;
}

interface Content {
  apd?: Record<string, string>;
  stages?: StagePlanStage[];
}

interface Plan {
  id: string;
  name: string;
  status: string;
  content_json: Content;
}

const newStage = (): StagePlanStage => ({
  id: crypto.randomUUID(),
  name: "",
  start: "",
  end: "",
  activities: "",
  contractors: "",
  risks: "",
  responsible: "",
});

export const StagePlan: React.FC<Props> = ({ projectId }) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = async () => {
    const { data, error } = await supabase
      .from("stage_plans")
      .select("id, name, status, content_json")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false });
    if (error) toast.error("Kunde inte läsa skedesplanerna");
    else setPlans((data || []).map((p: any) => ({ ...p, content_json: p.content_json || {} })));
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const create = async () => {
    const { data, error } = await supabase
      .from("stage_plans")
      .insert({ project_id: projectId, content_json: { apd: {}, stages: [newStage()] } as any })
      .select("id")
      .single();
    if (error) return toast.error("Kunde inte skapa skedesplanen");
    await load();
    setOpenId(data.id);
  };

  const update = async (id: string, patch: Partial<Plan>) => {
    setPlans((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    const { error } = await supabase.from("stage_plans").update(patch as any).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const setContent = (p: Plan, content: Content) => update(p.id, { content_json: content });

  const remove = async (id: string) => {
    setPlans((prev) => prev.filter((p) => p.id !== id));
    await supabase.from("stage_plans").delete().eq("id", id);
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar skedesplaner...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="26"
        exportHistory={{ documentType: "skedesplan" }}
        title="Skedesplan och APD-plan"
        description="Bas-U:s plan för hur arbetet fasas i tid och rum, med samtidiga arbeten och gemensamma skydd."
        icon={CalendarRange}
        actions={<Button onClick={create}><Plus className="h-4 w-4 mr-1" />Ny skedesplan</Button>}
      />

      {plans.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <CalendarRange className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Ingen skedesplan upprättad ännu.</p>
            <Button onClick={create}>Skapa skedesplan</Button>
          </CardContent>
        </Card>
      ) : (
        plans.map((p) => {
          const isOpen = openId === p.id;
          const stages = p.content_json.stages || [];
          const apd = p.content_json.apd || {};
          const filledApd = APD_FIELDS.filter((f) => (apd[f.key] || "").trim()).length;

          const updateStage = (stageId: string, patch: Partial<StagePlanStage>) =>
            setContent(p, {
              ...p.content_json,
              stages: stages.map((s) => (s.id === stageId ? { ...s, ...patch } : s)),
            });

          return (
            <Card key={p.id}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button className="text-left" onClick={() => setOpenId(isOpen ? null : p.id)}>
                    <CardTitle className="text-base">{p.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {stages.length} skeden · {filledApd}/{APD_FIELDS.length} APD-punkter
                    </p>
                  </button>
                  <div className="flex items-center gap-2">
                    <Badge variant={p.status === "active" ? "default" : "outline"}>
                      {p.status === "active" ? "Gällande" : "Pågår"}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => update(p.id, { status: p.status === "active" ? "draft" : "active" })}
                    >
                      {p.status === "active" ? "Återöppna" : "Markera gällande"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateStagePlanWordDocument(p.name, p.content_json as any)}
                    >
                      <FileDown className="h-4 w-4 mr-1" />Word
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(p.id)}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              {isOpen && (
                <CardContent className="space-y-6">
                  <Input
                    defaultValue={p.name}
                    onBlur={(e) => update(p.id, { name: e.target.value || "Skedesplan / APD-plan" })}
                  />

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold">Skeden</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setContent(p, { ...p.content_json, stages: [...stages, newStage()] })}
                      >
                        <Plus className="h-4 w-4 mr-1" />Lägg till skede
                      </Button>
                    </div>

                    {stages.map((s, i) => (
                      <div key={s.id} className="rounded-lg border border-border/60 p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-muted-foreground">Skede {i + 1}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              setContent(p, { ...p.content_json, stages: stages.filter((x) => x.id !== s.id) })
                            }
                          >
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </div>
                        <div className="grid gap-2 sm:grid-cols-3">
                          <Input
                            placeholder="Namn på skedet"
                            defaultValue={s.name}
                            onBlur={(e) => updateStage(s.id, { name: e.target.value })}
                          />
                          <Input
                            type="date"
                            defaultValue={s.start ?? ""}
                            onBlur={(e) => updateStage(s.id, { start: e.target.value })}
                          />
                          <Input
                            type="date"
                            defaultValue={s.end ?? ""}
                            onBlur={(e) => updateStage(s.id, { end: e.target.value })}
                          />
                        </div>
                        <div className="grid gap-2 sm:grid-cols-2">
                          <div className="space-y-1">
                            <label className="text-xs font-medium">Arbetsmoment</label>
                            <Textarea
                              rows={2}
                              defaultValue={s.activities ?? ""}
                              onBlur={(e) => updateStage(s.id, { activities: e.target.value })}
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-medium">Samtidiga entreprenörer</label>
                            <Textarea
                              rows={2}
                              defaultValue={s.contractors ?? ""}
                              onBlur={(e) => updateStage(s.id, { contractors: e.target.value })}
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-medium">Riskpunkter i skedet</label>
                            <Textarea
                              rows={2}
                              defaultValue={s.risks ?? ""}
                              onBlur={(e) => updateStage(s.id, { risks: e.target.value })}
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-medium">Ansvarig</label>
                            <Input
                              defaultValue={s.responsible ?? ""}
                              onBlur={(e) => updateStage(s.id, { responsible: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">APD-förutsättningar</h4>
                    {APD_FIELDS.map((f) => (
                      <div key={f.key} className="space-y-1">
                        <label className="text-sm font-medium">{f.label}</label>
                        {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
                        <Textarea
                          rows={2}
                          defaultValue={apd[f.key] ?? ""}
                          onBlur={(e) =>
                            setContent(p, { ...p.content_json, apd: { ...apd, [f.key]: e.target.value } })
                          }
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })
      )}
    </div>
  );
};

export default StagePlan;
